import { Button, Input, Modal, Radio } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import s from './TaskFormModal.module.scss';

import type { FC } from 'react';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import type { ITaskCard, ITaskFormValues } from '../types';

const MAX_TITLE_LENGTH = 100;

const DEFAULT_VALUES: ITaskFormValues = {
  title: '',
  priority: 'medium',
  description: null,
  taskType: null,
};

export interface ITaskFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  patientName?: string;
  task?: ITaskCard | null;
  isSubmitting: boolean;
  onSubmit: (values: ITaskFormValues) => void;
  onCancel: () => void;
}

const TaskFormModal: FC<ITaskFormModalProps> = ({
  open,
  mode,
  patientName,
  task,
  isSubmitting,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const titleRef = useRef<TextAreaRef>(null);

  const initialValues = useMemo<ITaskFormValues>(
    () =>
      mode === 'edit' && task
        ? {
            title: task.title,
            priority: task.priority,
            description: task.description,
            taskType: task.taskType,
          }
        : DEFAULT_VALUES,
    [mode, task],
  );

  const [values, setValues] = useState<ITaskFormValues>(initialValues);
  const [touched, setTouched] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const isDirty = useMemo(() => {
    if (mode === 'create') {
      return values.title.trim() !== '' || values.priority !== 'medium';
    }
    return (
      values.title !== initialValues.title ||
      values.priority !== initialValues.priority ||
      values.description !== initialValues.description ||
      values.taskType !== initialValues.taskType
    );
  }, [values, initialValues, mode]);

  const titleError = useMemo(() => {
    if (!touched) return '';
    if (!values.title.trim()) {
      return t('tasksDrawer.taskModal.titleRequired');
    }
    return '';
  }, [values.title, touched, t]);

  const isValid = values.title.trim().length > 0;

  const handleSubmit = useCallback(() => {
    setTouched(true);
    if (!isValid) return;
    onSubmit({
      title: values.title.trim(),
      priority: values.priority,
      description: values.description,
      taskType: values.taskType,
    });
  }, [isValid, values, onSubmit]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onCancel();
  }, [isDirty, onCancel]);

  // Reset to defaults on close. handleAfterOpenChange re-initializes values
  // when the modal reopens, so this reset does not cause a flash in edit mode.
  const handleAfterClose = useCallback(() => {
    setValues(DEFAULT_VALUES);
    setTouched(false);
    setConfirmDiscardOpen(false);
  }, []);

  const handleAfterOpenChange = useCallback(
    (visible: boolean) => {
      if (visible && mode === 'edit' && task) {
        setValues({
          title: task.title,
          priority: task.priority,
          description: task.description,
          taskType: task.taskType,
        });
        setTouched(false);
      }
      if (visible && mode === 'create') {
        titleRef.current?.resizableTextArea?.textArea?.focus();
      }
    },
    [mode, task],
  );

  const modalTitle =
    mode === 'create'
      ? t('tasksDrawer.taskModal.createTitle')
      : t('tasksDrawer.taskModal.editTitle');

  const submitLabel =
    mode === 'create'
      ? t('tasksDrawer.createTask')
      : t('tasksDrawer.taskModal.save');

  return (
    <>
      <Modal
        open={confirmDiscardOpen}
        title={t('tasksDrawer.taskModal.discardChanges')}
        centered
        maskClosable={false}
        zIndex={1200}
        data-cy="task-discard-confirm-modal"
        footer={null}
        onCancel={() => setConfirmDiscardOpen(false)}
      >
        <p>{t('tasksDrawer.taskModal.discardMessage')}</p>
        <div className={s['form-footer']}>
          <Button
            onClick={() => setConfirmDiscardOpen(false)}
            data-cy="task-modal-keep-editing-button"
          >
            {t('tasksDrawer.taskModal.keepEditing')}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setConfirmDiscardOpen(false);
              onCancel();
            }}
            disabled={isSubmitting}
            data-cy="task-discard-confirm-button"
          >
            {t('tasksDrawer.taskModal.discard')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={open}
        title={modalTitle}
        centered
        destroyOnClose
        maskClosable={false}
        zIndex={1100}
        onCancel={handleCancel}
        afterClose={handleAfterClose}
        afterOpenChange={handleAfterOpenChange}
        footer={null}
        data-cy="task-form-modal"
      >
        <div className={s['form-body']}>
          {patientName && (
            <div className={s['form-field']}>
              <label htmlFor="task-patient-input" className={s['field-label']}>
                {t('tasksDrawer.taskModal.patientLabel')}
              </label>
              <Input
                id="task-patient-input"
                value={patientName}
                disabled
                data-cy="task-patient-input"
              />
            </div>
          )}

          <div className={s['form-field']}>
            <label htmlFor="task-title-input" className={s['field-label']}>
              {t('tasksDrawer.taskModal.titleLabel')}{' '}
              <span className={s.required}>*</span>
            </label>
            <Input.TextArea
              ref={titleRef}
              id="task-title-input"
              value={values.title}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/\n/g, ' ');
                setValues((prev) => ({ ...prev, title: sanitized }));
                if (!touched) setTouched(true);
              }}
              maxLength={MAX_TITLE_LENGTH}
              showCount
              placeholder={t('tasksDrawer.taskModal.titlePlaceholder')}
              status={titleError ? 'error' : undefined}
              autoSize={{ minRows: 2 }}
              data-cy="task-title-input"
            />
            {titleError && (
              <p className={s['field-error']} data-cy="task-title-error">
                {titleError}
              </p>
            )}
          </div>

          <div className={s['form-field']}>
            <label className={s['field-label']}>
              {t('tasksDrawer.taskModal.priorityLabel')}{' '}
              <span className={s.required}>*</span>
            </label>
            <Radio.Group
              className={s['priority-radio']}
              value={values.priority}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, priority: e.target.value }))
              }
              data-cy="task-priority-group"
            >
              <Radio value="high">{t('tasksDrawer.priorityHigh')}</Radio>
              <Radio value="medium">{t('tasksDrawer.priorityMedium')}</Radio>
              <Radio value="low">{t('tasksDrawer.priorityLow')}</Radio>
            </Radio.Group>
          </div>
        </div>

        <div className={s['form-footer']}>
          <Button onClick={handleCancel} data-cy="task-modal-cancel-button">
            {t('tasksDrawer.taskModal.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
            data-cy="task-modal-submit-button"
          >
            {submitLabel}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default TaskFormModal;
